import { fitTitle, fitDescription } from "@/lib/seo-text";
import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import {
  FIRM,
  MICHAEL_PHONE,
  MICHAEL_PHONE_HREF,
  PENINSULA_ADDRESS,
  faqSchema,
  peninsulaBusinessSchema,
} from "@/lib/firm";

const DESCRIPTION = `Wills, enduring powers of attorney and estate administration for Scarborough and the Redcliffe Peninsula. Fixed fees — simple will from $660, will and EPOA pack $825, couples pack $1,499, all incl. GST. Home visits across the peninsula. Call Michael Klein on ${MICHAEL_PHONE}.`;

const FAQS = [
  {
    q: "What does a will cost in Scarborough?",
    a: "A simple will is from $660 including GST per person. A will plus enduring power of attorney is $825, and a couples pack — two wills and two enduring powers of attorney — is $1,499, both including GST. Wills that involve complex estate planning issues such as blended families, companies, trusts or a self-managed super fund are quoted separately in writing before we start.",
  },
  {
    q: "Can you come to me?",
    a: `Yes. Michael Klein lives on the peninsula and visits clients at home, in a unit, in hospital or in aged care anywhere from Clontarf to Newport at no extra charge. The office at ${PENINSULA_ADDRESS} is available by appointment, including evenings and weekends.`,
  },
  {
    q: "Do I need a new will if I have downsized into a unit?",
    a: "Usually yes, or at least a review. Downsizing changes what the estate actually holds: the family home may be gone, there may be a large cash balance, a lifetime lease or over-50s contract, and gifts in the old will may now fail because the asset named no longer exists. A will that gifts \"my house at ...\" leaves nothing if that house has been sold.",
  },
  {
    q: "What happens if I lose capacity without an enduring power of attorney?",
    a: "Nobody can sell your unit, deal with your bank or make health decisions for you automatically — not even your spouse or children. An application to the Queensland Civil and Administrative Tribunal is needed to appoint an administrator or guardian, which takes months and costs far more than the document would have. This is the single most common problem we see on the peninsula.",
  },
  {
    q: "Where is my original will kept?",
    a: "In our fireproof safe custody at no charge, with a copy provided to you and a note of where the original is held. We can also collect an original held by a firm you no longer deal with.",
  },
  {
    q: "Someone has died — what do we do first?",
    a: "Do not damage, unstaple or write on the original will. Locate it, get several certified copies of the death certificate, and call us before dealing with any asset. We handle probate and property transmissions on fixed fees agreed up front.",
  },
];

const PRICES = [
  { t: "Simple will*", d: "Per person, incl. GST, free safe custody of the original", p: "from $660" },
  { t: "Will + enduring power of attorney*", d: "Per person, incl. GST", p: "$825" },
  { t: "Couples pack*", d: "Two wills and two enduring powers of attorney, incl. GST", p: "$1,499" },
];

const LOCAL_ISSUES = [
  {
    t: "Unit and over-50s living",
    d: "Much of Scarborough's retiree population lives in strata units along the esplanade or in over-50s and retirement village communities. Those interests do not all pass the same way. A lifetime lease or licence in a village is not the same asset as a lot on title, exit fees and reinstatement obligations can consume a large share of what the family expects to receive, and the estate needs a will that accounts for that rather than assuming a house will be sold.",
  },
  {
    t: "Superannuation and life insurance",
    d: "Super does not automatically pass under a will. Without a valid binding death benefit nomination the trustee decides who receives it, and nominations lapse after three years unless they are non-lapsing. We check the nomination at the same time as the will, because it is frequently the largest single asset and the most common cause of a family dispute.",
  },
  {
    t: "Blended families and second marriages",
    d: "Marriage revokes an earlier will in Queensland unless it was made in contemplation of that marriage. Where there are children from a first relationship and a new partner living in the home, mirror wills are usually the wrong answer — a life interest or a testamentary trust protects both sides. This is quoted separately because it is genuine planning work, not a form.",
  },
  {
    t: "Estate sales of peninsula property",
    d: "A large share of Scarborough sales are estate sales. Selling as an executor needs the grant of representation and a transmission application on title before settlement can happen, and getting that sequence wrong delays settlement and risks default. We run the estate and the conveyance together rather than referring one out.",
  },
];

export const Route = createFileRoute("/scarborough-wills-and-estates")({
  head: () => ({
    meta: [
      { title: fitTitle(`Wills & Estate Lawyers Scarborough QLD | ${FIRM.name}`) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      {
        property: "og:title",
        content: fitTitle(`Wills, EPOAs and estates — Scarborough | ${FIRM.name}`),
      },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/scarborough-wills-and-estates" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/scarborough-wills-and-estates" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(peninsulaBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: ScarboroughWills,
});

function ScarboroughWills() {
  return (
    <>
      <PageHero
        eyebrow="Scarborough & the Redcliffe Peninsula"
        title="Wills, enduring powers of attorney and estates in Scarborough"
        lead={`Fixed-fee wills and estate work from our peninsula office at ${PENINSULA_ADDRESS}, with home, unit, hospital and aged care visits at no extra charge. Legal Practice Director Michael Klein lives locally, was admitted in 2003 and spent many years as managing partner of GKS Law.`}
      />

      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="rounded-sm border border-border bg-card p-7">
          <h2 className="font-serif text-2xl text-foreground">Fixed fees</h2>
          <dl className="mt-6 space-y-4 text-sm leading-relaxed">
            {PRICES.map((row, i) => (
              <div
                key={row.t}
                className={`flex flex-wrap items-baseline justify-between gap-2 ${
                  i < PRICES.length - 1 ? "border-b border-border/60 pb-4" : ""
                }`}
              >
                <dt className="text-foreground">
                  {row.t}
                  <span className="block text-muted-foreground">{row.d}</span>
                </dt>
                <dd className="font-serif text-2xl text-primary">{row.p}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            *Wills that involve complex estate planning issues — blended families, companies, trusts
            or a self-managed super fund — are quoted separately, in writing, before we start. Estate
            administration and probate is quoted on our fixed-fee{" "}
            <Link to="/probate" className="font-semibold text-primary underline">
              probate
            </Link>{" "}
            scale.
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">
            What actually matters on the peninsula
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {LOCAL_ISSUES.map((s) => (
              <div key={s.t} className="rounded-sm border border-border bg-card p-6">
                <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-3">
          <Link
            to="/scarborough"
            className="rounded-sm border border-border bg-card p-6 transition-colors hover:border-primary"
          >
            <span className="font-serif text-xl text-foreground">Scarborough office</span>
            <span className="mt-2 block text-sm text-muted-foreground">
              Everything we do from {PENINSULA_ADDRESS}.
            </span>
          </Link>
          <Link
            to="/estate-disputes/$suburb"
            params={{ suburb: "scarborough" }}
            className="rounded-sm border border-border bg-card p-6 transition-colors hover:border-primary"
          >
            <span className="font-serif text-xl text-foreground">Estate disputes</span>
            <span className="mt-2 block text-sm text-muted-foreground">
              Family provision claims and will challenges, run locally.
            </span>
          </Link>
          <Link
            to="/body-corporate-scarborough"
            className="rounded-sm border border-border bg-card p-6 transition-colors hover:border-primary"
          >
            <span className="font-serif text-xl text-foreground">Unit &amp; body corporate</span>
            <span className="mt-2 block text-sm text-muted-foreground">
              By-laws, levies, committees and buying into a scheme.
            </span>
          </Link>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Questions people ask</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>

        <div className="mt-14 rounded-sm border border-border bg-card p-7">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Prefer to talk it through first? Michael Klein's direct line is{" "}
            <a href={MICHAEL_PHONE_HREF} className="font-semibold text-primary underline">
              {MICHAEL_PHONE}
            </a>
            . Nearby:{" "}
            <Link to="/wills/$suburb" params={{ suburb: "redcliffe" }} className="font-semibold text-primary underline">
              Redcliffe wills
            </Link>{" "}
            and{" "}
            <Link to="/wills/$suburb" params={{ suburb: "newport" }} className="font-semibold text-primary underline">
              Newport wills
            </Link>
            .
          </p>
          <div className="mt-6">
            <EnquiryForm heading="Book a Scarborough will appointment" />
            <p className="mt-4 text-center text-sm leading-relaxed text-muted-foreground">
              Administering an estate after a death? The{" "}
              <Link to="/executor-intake" className="font-semibold text-primary underline">
                executor intake form
              </Link>{" "}
              captures what we need to tell you whether a grant is required and quote a fixed fee.
            </p>
            <div className="mt-8">
              <ExecutorIntakeForm heading="Start a probate enquiry" />
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
